import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { UsuarioContext } from '../providers/personaContext';
import { Card as UICard, CardContent as UICardContent } from '../../components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '../../components/ui/avatar';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WorkIcon from '@mui/icons-material/Work';
import SchoolIcon from '@mui/icons-material/School';
import PeopleIcon from '@mui/icons-material/People';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CakeIcon from '@mui/icons-material/Cake';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import SettingsRemoteIcon from '@mui/icons-material/SettingsRemote';
import { ToastContainer, toast } from 'react-toastify';
import { fetchDashboardStats } from '../../peticiones_API/PeticionesVacaciones';
import { fetchProximosCumpleanos } from '../../peticiones_API/PeticionesGestionPersonasPersonas';
import useIntervencionesDisponibilidad from './intervenciones/hooks/useIntervencionesDisponibilidad';
import { confetti } from '@tsparticles/confetti';
import GorraCumpleanos from '../img/gorra_cumpleaños.png';
import { Grid, Card, CardContent, Typography, Box, LinearProgress } from '@mui/material';



const API_URL = process.env.REACT_APP_API_URL;

// Convocatoria = sistema SEPE legacy. Retirado del acceso directo el 2026-07-27
// (coherente con Sidebar.js). Las rutas /convocatoria/* siguen vivas por URL directa.
// Para reactivar el acceso desde la landing, cambiar a true.
const MOSTRAR_CONVOCATORIA = false;

function GestorVacacionesDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { persona } = useContext(UsuarioContext);
  const [stats, setStats] = useState(null);
  const [cumpleanos, setCumpleanos] = useState([]);
  const [loading, setLoading] = useState(true);

  const esAdministrador = ['administrador', 'super administrador'].includes(persona?.rol?.toLowerCase() || '');
  const rolLower = persona?.rol?.toLowerCase() || '';
  const puedeVerCursos = ['administrador', 'gestor de contenidos', 'super administrador'].includes(rolLower);
  const tieneAccesoReservas = persona?.reservas || false;
  const esPersonaColaboradora = persona?.es_persona_colaboradora || false;
  const empresaVacaciones = persona?.empresa_vacaciones !== false;
  const tieneVacaciones = !esPersonaColaboradora || empresaVacaciones;

  // Participación en Citas Técnicas (reutiliza GET /mi-disponibilidad). El
  // acceso rápido "Citas Técnicas" solo se ofrece a quien participa en una
  // campaña activa como usuario. `loading` evita mostrarlo en el primer paint.
  const { esUsuarioActivo, loading: cargandoDisponibilidad } = useIntervencionesDisponibilidad();

  const cargarDatos = useCallback(async () => {
    try {
      setLoading(true);
      const [data, cumples] = await Promise.all([
        fetchDashboardStats(),
        fetchProximosCumpleanos(3)
      ]);
      setStats(data);
      setCumpleanos(cumples || []);
    } catch (error) {
      console.error('Error al cargar estadisticas:', error);
      toast.error('Error al cargar las estadisticas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (persona?.id) {
      cargarDatos();
    }
  }, [cargarDatos, persona?.id]);

  useEffect(() => {
    confetti.init();
  }, []);

  // Aviso del guard: si ProtectedRoute mandó aquí a un autenticado sin permiso,
  // llega un mensaje en el estado de navegación. Lo mostramos y limpiamos el
  // estado (replace) para que no reaparezca al refrescar o re-renderizar.
  useEffect(() => {
    if (location.state?.avisoAcceso) {
      toast.warn(location.state.avisoAcceso);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const calcularPorcentaje = (usados, totales) => {
    if (!totales) return 0;
    return Math.min((usados / totales) * 100, 100);
  };

  if (loading || !persona?.id) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="h-[50px] w-[50px] rounded-full border-4 border-gray-200 border-t-[#336385] animate-spin" />
        <p className="mt-4 text-muted-foreground">Cargando datos...</p>
      </div>
    );
  }

  const tieneCalendarioLaboral = !!stats?.calendario_laboral;
  const diasTotales = tieneCalendarioLaboral ? (stats?.dias_totales || 0) : 0;
  const diasUsados = tieneCalendarioLaboral ? (stats?.dias_usados || 0) : 0;
  const diasDisponibles = diasTotales - diasUsados;
  const porcentajeUsado = calcularPorcentaje(diasUsados, diasTotales);
  const diasVeranoObligatorios = tieneCalendarioLaboral ? (stats?.dias_verano_obligatorios || 0) : 0;
  const diasVeranoUsados = tieneCalendarioLaboral ? (stats?.dias_verano_usados || 0) : 0;
  const diasVeranoDisponibles = diasVeranoObligatorios - diasVeranoUsados;
  const porcentajeVeranoUsado = calcularPorcentaje(diasVeranoUsados, diasVeranoObligatorios);

  const accesosRapidos = [];
  if (MOSTRAR_CONVOCATORIA && puedeVerCursos) {
    accesosRapidos.push({ id: 'cursos', label: 'Cursos', icon: SchoolIcon, path: '/convocatoria/cursos' });
  }
  if (!persona?.es_colaborador) {
    accesosRapidos.push({ id: 'personas', label: 'Personas', icon: PeopleIcon, path: '/gestionpersonas/personas' });
  }
  if (!persona?.es_colaborador) {
    accesosRapidos.push({ id: 'wiki', label: 'Wiki', icon: MenuBookIcon, path: '/documentos/wiki' });
  }
  if (tieneAccesoReservas || esAdministrador) {
    accesosRapidos.push({ id: 'inventario', label: 'Utilidades', icon: Inventory2Icon, path: '/utilidades' });
  }
  if (tieneVacaciones) {
    accesosRapidos.push({ id: 'vacaciones', label: 'Vacaciones', icon: BeachAccessIcon, path: '/panel/vacaciones/calendario' });
  }
  // Citas Técnicas: acceso PERSONAL a "Mis Citas". Solo se muestra si el
  // usuario participa en una campaña activa (es_usuario_activo), y SIEMPRE lleva
  // a /citas-tecnicas/mis-campanas. Antes se añadía incondicionalmente (salía
  // sin campaña) y para admin apuntaba a /citas-tecnicas/dashboard, que un admin
  // sin `dashboard.view` no puede abrir → el guard rebotaba a /panel-inicio.
  if (esUsuarioActivo && !cargandoDisponibilidad) {
    accesosRapidos.push({ id: 'intervenciones', label: 'Citas Técnicas', icon: SettingsRemoteIcon, path: '/citas-tecnicas/mis-campanas' });
  }

  const totalAccesos = accesosRapidos.length;
  const getColsClass = () => {
    if (totalAccesos === 1) return 'grid-cols-1';
    if (totalAccesos === 2) return 'grid-cols-2';
    if (totalAccesos === 3) return 'grid-cols-2 sm:grid-cols-3';
    if (totalAccesos === 4) return 'grid-cols-2 sm:grid-cols-4';
    if (totalAccesos === 5) return 'grid-cols-2 sm:grid-cols-6';
    return 'grid-cols-2 sm:grid-cols-3';
  };
  const getSpanClass = (idx) => {
    if (totalAccesos === 5) return idx < 3 ? 'sm:col-span-2' : 'sm:col-span-3';
    return '';
  };

  const getBirthdayVariant = (dias) => {
    if (dias === 0) return 'success';
    if (dias <= 7) return 'warning';
    return 'secondary';
  };

  const statCards = [
    {
      icon: BeachAccessIcon,
      title: 'Días Por Aprobar',
      value: diasDisponibles,
      caption: `de ${diasTotales} días totales`,
      progress: porcentajeUsado,
      gradient: tieneCalendarioLaboral
        ? 'bg-gradient-to-br from-[#667eea] to-[#764ba2] hover:from-[#665eea] hover:to-[#762ba2]'
        : 'bg-gradient-to-br from-gray-400 to-gray-500 opacity-70',
      clickable: tieneCalendarioLaboral,
      path: '/panel/vacaciones/solicitar',
    },
    {
      icon: WbSunnyIcon,
      title: 'Días Verano Por Aprobar',
      value: diasVeranoDisponibles,
      caption: `de ${diasVeranoObligatorios} obligatorios`,
      progress: porcentajeVeranoUsado,
      gradient: tieneCalendarioLaboral
        ? 'bg-gradient-to-br from-[#f093fb] to-[#f5576c] hover:from-[#f073fb] hover:to-[#f5376c]'
        : 'bg-gradient-to-br from-gray-400 to-gray-500 opacity-70',
      clickable: tieneCalendarioLaboral,
      path: '/panel/vacaciones/solicitar',
      extra: stats?.periodo_verano ? (
        <span className="block mt-2 text-xs text-white/80">
          Período: {new Date(stats.periodo_verano.fecha_inicio + 'T00:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })} - {new Date(stats.periodo_verano.fecha_fin + 'T00:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
        </span>
      ) : null,
    },
    {
      icon: PendingActionsIcon,
      title: 'Pendientes',
      value: stats?.solicitudes_pendientes || 0,
      caption: 'solicitudes por aprobar',
      gradient: 'bg-gradient-to-br from-[#4facfe] to-[#00f2fe]',
    },
    {
      icon: CheckCircleIcon,
      title: 'Aprobadas',
      value: stats?.solicitudes_aprobadas || 0,
      caption: 'solicitudes este año',
      gradient: 'bg-gradient-to-br from-[#11998e] to-[#38ef7d]',
    },
  ];

  return (
    <div className="p-5 max-w-[1400px] mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-[2.125rem] font-bold text-[#303248] leading-tight mb-1">
          Bienvenido, {persona?.nombre || 'Usuario'}!
        </h1>
      </div>

      {/* Accesos Rápidos + Cumpleaños */}
      <div className={`grid grid-cols-1 ${persona?.es_colaborador ? '' : 'md:grid-cols-2'} gap-6 mb-8`}>
        <UICard className={`h-full rounded-md ${persona?.es_colaborador ? 'md:col-span-2' : ''}`}>
          <UICardContent className="p-4">
            <h2 className="text-[1.25rem] font-bold text-[#336385] mb-4">
              Accesos Rápidos
            </h2>
            <div className={`grid gap-4 justify-items-stretch ${getColsClass()}`}>
              {accesosRapidos.map((acceso, index) => {
                const IconComponent = acceso.icon;
                return (
                  <UICard
                    key={acceso.id}
                    onClick={() => navigate(acceso.path)}
                    className={`cursor-pointer text-center py-4 rounded-md transition-all duration-200 hover:-translate-y-1 hover:shadow-[0px_8px_25px_rgba(0,0,0,0.15)] ${getSpanClass(index)}`}
                  >
                    <UICardContent className="p-4">
                      <IconComponent sx={{ fontSize: 40, color: '#336385', mb: 1 }} />
                      <p className="font-bold text-[#303248]">{acceso.label}</p>
                    </UICardContent>
                  </UICard>
                );
              })}
            </div>
          </UICardContent>
        </UICard>

        {!persona?.es_colaborador && (
          <UICard className="h-full rounded-md">
            <UICardContent className="p-4">
              <h2 className="flex items-center gap-2 text-[1.25rem] font-bold text-[#336385] mb-4">
                <CakeIcon /> Próximos Cumpleaños
              </h2>
              {cumpleanos && cumpleanos.length > 0 ? (
                <div className="flex flex-col gap-4">
                  {cumpleanos.map((cumple, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-gray-100 rounded-lg cursor-pointer transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_6px_16px_rgba(51,99,133,0.25)]"
                      onMouseEnter={cumple.dias_restantes === 0 ? (e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const x = ((rect.left + rect.width / 2) / window.innerWidth) * 100;
                        const y = ((rect.top + rect.height / 2) / window.innerHeight) * 100;
                        confetti({
                          count: 200,
                          spread: 200,
                          position: { x, y },
                          colors: ['#ff6b6b', '#ffd93d', '#6bcb77', '#4d96ff', '#ff9ff3', '#48dbfb'],
                          ticks: 150,
                          gravity: 0.8,
                          scalar: 0.9,
                          disableForReducedMotion: false
                        });
                      } : undefined}
                    >
                      <div className="flex items-center gap-3">
                        <div className="relative inline-flex">
                          <Avatar className="h-10 w-10 bg-[#336385]">
                            <AvatarImage src={cumple.foto ? `${API_URL}/api/persona/imagen/${cumple.foto}` : null} />
                            <AvatarFallback className="bg-[#336385] text-white">
                              {cumple.nombre?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          {cumple.dias_restantes === 0 && (
                            <img
                              src={GorraCumpleanos}
                              alt="Cumpleaños"
                              className="absolute -top-[21px] -right-[12px] w-[30px] h-[30px] rotate-[25deg] pointer-events-none z-[1]"
                            />
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-bold">{cumple.nombre} {cumple.apellidos}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(cumple.fecha_cumple).toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })}
                          </p>
                        </div>
                      </div>
                      <Badge variant={getBirthdayVariant(cumple.dias_restantes)} className="gap-1 px-3 py-1.5">
                        <CakeIcon sx={{ fontSize: 14 }} />
                        {cumple.dias_restantes === 0 ? '¡Hoy!' : cumple.dias_restantes === 1 ? 'Mañana' : `${cumple.dias_restantes} días`}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center py-6 text-muted-foreground">
                  No hay cumpleaños próximos
                </p>
              )}
            </UICardContent>
          </UICard>
        )}
      </div>

      {/* Mis Vacaciones */}
      {tieneVacaciones && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <h2 className="text-[1.5rem] font-bold text-[#336385]">
                Mis Vacaciones
              </h2>
              {tieneCalendarioLaboral ? (
                <Badge variant="outline" className="gap-1 border-[#336385] text-[#336385] px-3 py-1">
                  <WorkIcon sx={{ fontSize: 16 }} />
                  {stats.calendario_laboral.nombre} - {stats.calendario_laboral.provincia}
                </Badge>
              ) : (
                <Badge variant="warning" className="gap-1 px-3 py-1">
                  <WorkIcon sx={{ fontSize: 16 }} />
                  Sin calendario laboral asignado
                </Badge>
              )}
            </div>
          </div>

          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <Card sx={{
                height: '100%',
                background: tieneCalendarioLaboral
                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                  : 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)',
                color: '#fff',
                transition: 'transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease',
                opacity: tieneCalendarioLaboral ? 1 : 0.7,
                ...(tieneCalendarioLaboral && {
                  '&:hover': {
                    cursor: 'pointer',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.25)',
                    background: 'linear-gradient(135deg, #665eea 0%, #762ba2 100%)'
                  }
                })
              }}
              onClick={() => tieneCalendarioLaboral && navigate('/panel/vacaciones/solicitar')}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <BeachAccessIcon sx={{ color: 'white', fontSize: 32, mr: 1 }} />
                    <Typography variant="body1" sx={{ color: 'white', fontWeight: 'bold' }}>
                      Días Por Aprobar
                    </Typography>
                  </Box>
                  <Typography variant="h3" sx={{ color: 'white', fontWeight: 'bold', mb: 0.5 }}>
                    {diasDisponibles}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    de {diasTotales} días totales
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={porcentajeUsado}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: 'rgba(255,255,255,0.3)',
                        '& .MuiLinearProgress-bar': { backgroundColor: 'white' }
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={6} md={3}>
              <Card sx={{
                height: '100%',
                background: tieneCalendarioLaboral
                  ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
                  : 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)',
                color: '#fff',
                transition: 'transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease',
                opacity: tieneCalendarioLaboral ? 1 : 0.7,
                ...(tieneCalendarioLaboral && {
                  '&:hover': {
                    cursor: 'pointer',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.25)',
                    background: 'linear-gradient(135deg, #f073fb 0%, #f5376c 100%)'
                  }
                })
              }}
              onClick={() => tieneCalendarioLaboral && navigate('/panel/vacaciones/solicitar')}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <WbSunnyIcon sx={{ color: 'white', fontSize: 32, mr: 1 }} />
                    <Typography variant="body1" sx={{ color: 'white', fontWeight: 'bold' }}>
                      Días Verano Por Aprobar
                    </Typography>
                  </Box>
                  <Typography variant="h3" sx={{ color: 'white', fontWeight: 'bold', mb: 0.5 }}>
                    {diasVeranoDisponibles > 0 ? diasVeranoDisponibles : '0'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    de {diasVeranoObligatorios} obligatorios
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={porcentajeVeranoUsado}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: 'rgba(255,255,255,0.3)',
                        '& .MuiLinearProgress-bar': { backgroundColor: 'white' }
                      }}
                    />
                  </Box>
                  {stats?.periodo_verano && (
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', display: 'block', mt: 1 }}>
                      Período: {new Date(stats.periodo_verano.fecha_inicio + 'T00:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })} - {new Date(stats.periodo_verano.fecha_fin + 'T00:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={6} md={3}>
              <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PendingActionsIcon sx={{ color: 'white', fontSize: 32, mr: 1 }} />
                    <Typography variant="body1" sx={{ color: 'white', fontWeight: 'bold' }}>
                      Pendientes
                    </Typography>
                  </Box>
                  <Typography variant="h3" sx={{ color: 'white', fontWeight: 'bold', mb: 0.5 }}>
                    {stats?.solicitudes_pendientes || 0}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    solicitudes por aprobar
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={6} md={3}>
              <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CheckCircleIcon sx={{ color: 'white', fontSize: 32, mr: 1 }} />
                    <Typography variant="body1" sx={{ color: 'white', fontWeight: 'bold' }}>
                      Aprobadas
                    </Typography>
                  </Box>
                  <Typography variant="h3" sx={{ color: 'white', fontWeight: 'bold', mb: 0.5 }}>
                    {stats?.solicitudes_aprobadas || 0}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    solicitudes este año
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          </>)}

      <ToastContainer />
    </div>
  );
}

export default GestorVacacionesDashboard;
